/**
 * OTLP/HTTP JSON exporter.
 *
 * Contract: this function never throws and never rejects. Tracing is
 * observability, not business logic — a collector outage, an expired Access
 * token or a DNS failure must be invisible to the request that produced the
 * span.
 */

import type { OtlpTracePayload, OtlpTraceResponse } from './types';

export type ExporterConfig = {
	/** Full URL including /v1/traces. */
	endpoint: string;
	/** Cloudflare Access service token. Both are required or the edge 403s. */
	accessClientId?: string;
	accessClientSecret?: string;
	/** Abort the export after this long. Keeps waitUntil from hanging. */
	timeoutMs?: number;
	/** Where to report export problems. Defaults to console. */
	onError?: (message: string, detail?: unknown) => void;
};

export type ExportResult = {
	ok: boolean;
	status?: number;
	/** Set when the collector reported a non-empty partialSuccess. */
	rejectedSpans?: string;
	error?: string;
};

const DEFAULT_TIMEOUT_MS = 5000;

export async function exportTraces(payload: OtlpTracePayload, config: ExporterConfig): Promise<ExportResult> {
	const report = config.onError ?? ((message: string, detail?: unknown) => console.error(message, detail ?? ''));

	if (!config.endpoint) {
		return { ok: false, error: 'otel: no endpoint configured' };
	}
	// Both Access headers or neither — sending one yields a 403 that looks like
	// a collector fault. Fail fast and loudly at config level instead.
	if (Boolean(config.accessClientId) !== Boolean(config.accessClientSecret)) {
		report('otel: CF Access client id/secret must both be set — export skipped');
		return { ok: false, error: 'otel: incomplete Access credentials' };
	}

	const headers: Record<string, string> = { 'Content-Type': 'application/json' };
	if (config.accessClientId && config.accessClientSecret) {
		headers['CF-Access-Client-Id'] = config.accessClientId;
		headers['CF-Access-Client-Secret'] = config.accessClientSecret;
	}

	// AbortSignal.timeout is available in the Workers runtime and all supported
	// browsers; guarded because it is absent in older test environments.
	const signal = typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal
		? AbortSignal.timeout(config.timeoutMs ?? DEFAULT_TIMEOUT_MS)
		: undefined;

	try {
		const response = await fetch(config.endpoint, {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
			signal,
		});

		if (!response.ok) {
			// 403 here almost always means the Access service token is wrong or
			// rotated, so say that rather than just printing the status.
			const hint = response.status === 403 ? ' (check CF Access service token)' : '';
			const body = await safeText(response);
			report(`otel: export failed HTTP ${response.status}${hint}`, body);
			return { ok: false, status: response.status, error: `HTTP ${response.status}` };
		}

		// A 200 does not mean every span was stored. partialSuccess is the only
		// place the collector reports drops (bad id lengths, schema mismatch).
		const result = (await safeJson(response)) as OtlpTraceResponse | null;
		const rejected = result?.partialSuccess?.rejectedSpans;
		if (rejected && rejected !== '0') {
			report(`otel: collector rejected ${rejected} span(s)`, result?.partialSuccess?.errorMessage);
			return { ok: true, status: response.status, rejectedSpans: rejected };
		}
		if (result?.partialSuccess?.errorMessage) {
			report('otel: collector returned partialSuccess', result.partialSuccess.errorMessage);
		}

		return { ok: true, status: response.status };
	} catch (error) {
		const message = error instanceof Error ? error.message : String(error);
		report('otel: export threw', message);
		return { ok: false, error: message };
	}
}

async function safeText(response: Response): Promise<string | undefined> {
	try {
		return (await response.text()).slice(0, 500);
	} catch {
		return undefined;
	}
}

async function safeJson(response: Response): Promise<unknown> {
	try {
		return await response.json();
	} catch {
		return null;
	}
}
